import { apolloClient } from "@App/graphql/client";
import {
  GET_ORGANIZATION_PREMIUM_SERVICES,
  type OrganizationPremiumServicesQuery,
} from "@App/graphql/queries/getOrganizationPremiumServices";
import { useServices } from "@App/hooks/api/useServices";
import { actionCreators, type State } from "@App/state";
import type { PremiumService, Service } from "@App/types";
import { PremiumServiceTypes } from "@App/types/enums";
import { PremiumServiceTypeIsSubService } from "@App/utils/conditionals/services";
import { bindActionCreators } from "@reduxjs/toolkit";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

export const useDriverData = () => {
  const { services } = useServices();
  const organization = useSelector((state: State) => state.orders.organization);
  const driverResponse = useSelector((state: State) => state.auth.driverData);
  const presetFilters = useSelector((state: State) => state.filters.presetFilters);
  const premiumServices = useSelector((state: State) => state.orders.premiumServices);

  const [requiredServices, setRequiredServices] = useState<Service[]>([]);
  const [requiredPremiumServices, setRequiredPremiumServices] = useState<PremiumService[]>([]);
  const [optionalServices, setOptionalServices] = useState<Service[]>([]);
  const [optionalPremiumServices, setOptionalPremiumServices] = useState<PremiumService[]>([]);
  const [prohibitedServices, setProhibitedServices] = useState<Service[]>([]);
  const [prohibitedPremiumServices, setProhibitedPremiumServices] = useState<PremiumService[]>([]);

  const [availablePremiumServices, setAvailablePremiumServices] = useState<PremiumService[] | any>(
    [],
  );
  const [availablePremiumServicesLoading, setAvailablePremiumServicesLoading] =
    useState<boolean>(false);

  const dispatch = useDispatch();
  const { chooseService, addPremiumService } = bindActionCreators(actionCreators, dispatch);

  useEffect(() => {
    if (!driverResponse || !services || !premiumServices) return;

    setRequiredServices(
      createRequiredServiceArray(driverResponse.serviceCatalog.required?.services),
    );
    setRequiredPremiumServices(
      createRequiredPremiumServiceArray(driverResponse.serviceCatalog.required?.premiumServices),
    );
    setOptionalServices(
      createOptionalServiceArray(driverResponse.serviceCatalog.optional?.services),
    );
    setOptionalPremiumServices(
      createOptionalPremiumServiceArray(driverResponse.serviceCatalog.optional?.premiumServices),
    );
    setProhibitedServices(
      createOptionalServiceArray(driverResponse.serviceCatalog.prohibited?.services),
    );
    setProhibitedPremiumServices(
      createProhibitedPremiumServiceArray(
        driverResponse.serviceCatalog.prohibited?.premiumServices,
      ),
    );
  }, [driverResponse, services, availablePremiumServices]);

  const getPremiumServices = () => {
    setAvailablePremiumServicesLoading(true);
    if (services?.length > 0) {
      apolloClient
        .query<OrganizationPremiumServicesQuery>({
          query: GET_ORGANIZATION_PREMIUM_SERVICES,
          variables: {
            organization: organization.id,
            services: [...new Set(services.map((service: Service) => service.id))],
            driver: driverResponse?.uuid ?? null,
            network: driverResponse?.network?.id ?? null,
            ...(presetFilters &&
              presetFilters.length > 0 && {
                presetFilters: presetFilters,
              }),
          },
        })
        .then((res) => {
          const premiumServices = res.data.organization.premium_services;
          setAvailablePremiumServices(premiumServices);
          setAvailablePremiumServicesLoading(false);
        });
    }
  };

  useEffect(() => {
    getPremiumServices();
  }, [services]);

  const createRequiredServiceArray = (serviceCollection) => {
    const requiredServices: Service[] = [];
    if (serviceCollection?.length > 0) {
      serviceCollection.map((unsanitizedService: any) => {
        const service = services?.find(
          (service: Service) => service.id === unsanitizedService.id.toString(),
        );
        if (service) {
          requiredServices.push(service);
          chooseService(service);
        }
      });
    }
    return removeServicesThatShouldBeHidden(requiredServices);
  };

  const createOptionalServiceArray = (serviceCollection) => {
    const optionalServices: Service[] = [];
    if (serviceCollection?.length > 0) {
      serviceCollection.map((unsanitizedService: any) => {
        const service = services?.find(
          (service: Service) => service.id === unsanitizedService.id.toString(),
        );
        if (service) {
          optionalServices.push(service);
        }
      });
    }
    return removeServicesThatShouldBeHidden(optionalServices);
  };

  const createRequiredPremiumServiceArray = (serviceCollection) => {
    const requiredPremiumServices: PremiumService[] = [];
    if (serviceCollection?.length > 0) {
      serviceCollection.map((unsanitizedService: any) => {
        const service = availablePremiumServices?.find(
          (service: Service) => service.id === unsanitizedService.id.toString(),
        );
        if (service && PremiumServiceTypeIsSubService(service)) {
          requiredPremiumServices.push(service);
          addPremiumService(service);
        }
      });
    }
    return removePremiumServicesThatShouldBeHidden(requiredPremiumServices);
  };

  const createOptionalPremiumServiceArray = (serviceCollection) => {
    const optionalPremiumServices: PremiumService[] = [];
    if (serviceCollection?.length > 0) {
      serviceCollection.map((unsanitizedService: any) => {
        const service = availablePremiumServices?.find(
          (service: Service) => service.id === unsanitizedService.id.toString(),
        );
        if (service && PremiumServiceTypeIsSubService(service)) {
          optionalPremiumServices.push(service);
        }
      });
    }
    return removePremiumServicesThatShouldBeHidden(optionalPremiumServices);
  };

  const createProhibitedPremiumServiceArray = (serviceCollection) => {
    const prohibitedPremiumServices: PremiumService[] = [];
    if (serviceCollection?.length > 0) {
      serviceCollection.map((unsanitizedService: any) => {
        const service = availablePremiumServices?.find(
          (service: Service) => service.id === unsanitizedService.id.toString(),
        );
        if (service) {
          prohibitedPremiumServices.push(service);
        }
      });
    }
    return removePremiumServicesThatShouldBeHidden(prohibitedPremiumServices);
  };

  const removeServicesThatShouldBeHidden = (serviceCollection: Service[]) => {
    return serviceCollection.filter((service: Service) => service.id !== "2");
  };

  const removePremiumServicesThatShouldBeHidden = (premiumServiceCollection: PremiumService[]) => {
    return premiumServiceCollection.filter(
      (premiumService: PremiumService) => premiumService.type !== PremiumServiceTypes.UVV,
    );
  };

  return {
    availablePremiumServices,
    availablePremiumServicesLoading,
    getPremiumServices,
    driverResponse,
    requiredServices,
    requiredPremiumServices,
    optionalServices,
    optionalPremiumServices,
    prohibitedServices,
    prohibitedPremiumServices,
  };
};
